import { Card } from '@mantine/core';
import type { CardProps } from '@mantine/core';
import { forwardRef } from 'react';
import classes from './MedleyCard.module.css';

export type MedleyCardVariant = 'default' | 'flat' | 'elevated';

export interface MedleyCardProps extends Omit<CardProps, 'withBorder' | 'radius'> {
  /** Card shadow variant */
  variant?: MedleyCardVariant;
  /** Enable hover effect with shadow enhancement */
  hoverable?: boolean;
}

/**
 * MedleyCard - Standardized card component (MED-129)
 *
 * A wrapper around Mantine Card with Medley design system defaults:
 * - 12px border radius
 * - White background
 * - No border
 * - Configurable shadow variants
 *
 * @example
 * // Default card with standard shadow
 * <MedleyCard>Content</MedleyCard>
 *
 * @example
 * // Flat card without shadow (for nested or subtle contexts)
 * <MedleyCard variant="flat">Content</MedleyCard>
 *
 * @example
 * // Elevated card with deeper shadow (for emphasis)
 * <MedleyCard variant="elevated">Content</MedleyCard>
 *
 * @example
 * // Hoverable card with interactive feedback
 * <MedleyCard hoverable>Clickable content</MedleyCard>
 */
export const MedleyCard = forwardRef<HTMLDivElement, MedleyCardProps>(
  ({ variant = 'default', hoverable = false, className, children, ...props }, ref) => {
    const variantClass = classes[variant];
    const hoverClass = hoverable ? classes.hoverable : '';
    const combinedClassName = [classes.card, variantClass, hoverClass, className]
      .filter(Boolean)
      .join(' ');

    return (
      <Card ref={ref} className={combinedClassName} withBorder={false} radius={12} {...props}>
        {children}
      </Card>
    );
  }
);

MedleyCard.displayName = 'MedleyCard';

export default MedleyCard;
